import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";

var _dec, _dec2, _class, _class2, _temp;

/*
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 - present Instructure, Inc.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { themeable } from '@instructure/ui-themeable';
import { passthroughProps, callRenderProp, deprecated } from '@instructure/ui-react-utils';
import { View } from '@instructure/ui-view';
var styles = {
  componentId: 'XOwIb',
  template: function template(theme) {
    return "\n\n.XOwIb_bGBk{-ms-user-select:none;-webkit-user-select:none;cursor:pointer;font-family:".concat(theme.fontFamily || 'inherit', ";font-size:").concat(theme.fontSize || 'inherit', ";font-weight:").concat(theme.fontWeight || 'inherit', ";line-height:").concat(theme.lineHeight || 'inherit', ";user-select:none}\n\n.XOwIb_bGBk[aria-disabled],.XOwIb_bGBk[aria-selected]{cursor:default}\n\n.XOwIb_bGBk[aria-disabled]{opacity:0.5}\n\n.XOwIb_bGBk:before{border-radius:0.25rem!important;inset:0.5rem!important}\n\n.XOwIb_bGBk:focus:before{inset:0.375rem!important}\n\n.XOwIb_ddES,.XOwIb_eLeB{white-space:nowrap}\n\n.XOwIb_eLeB{-webkit-margin-end:0.2em;border:0.0625rem solid transparent;border-top-left-radius:0.1875rem;border-top-right-radius:0.1875rem;color:").concat(theme.secondaryColor || 'inherit', ";margin-bottom:-0.0625rem;margin-inline-end:0.2em;padding:0.75rem 1rem;position:relative}\n\n[dir=ltr] .XOwIb_eLeB{margin-right:0.2em}\n\n[dir=rtl] .XOwIb_eLeB{margin-left:0.2em}\n\n.XOwIb_eLeB:first-of-type{-webkit-margin-start:0;margin-inline-start:0}\n\n[dir=ltr] .XOwIb_eLeB:first-of-type{margin-left:0}\n\n[dir=rtl] .XOwIb_eLeB:first-of-type{margin-right:0}\n\n.XOwIb_eLeB:not([aria-selected]):not([aria-disabled]):hover,.XOwIb_eLeB[aria-selected]{background:").concat(theme.secondarySelectedBackground || 'inherit', ";border-color:").concat(theme.secondarySelectedBorderColor || 'inherit', "}\n\n.XOwIb_eLeB:hover,.XOwIb_eLeB[aria-disabled],.XOwIb_eLeB[aria-selected]{color:").concat(theme.secondarySelectedColor || 'inherit', "}\n\n.XOwIb_eLeB[aria-selected]{border-bottom-color:").concat(theme.secondarySelectedBackground || 'inherit', ";z-index:").concat(theme.zIndex || 'inherit', "}\n\n@keyframes XOwIb_epeh{to{opacity:1;transform:translateZ(0) scaleX(1)}}\n\n.XOwIb_ddES{color:").concat(theme.defaultColor || 'inherit', ";line-height:1;padding:1rem 1.25rem;position:relative;z-index:").concat(theme.zIndex || 'inherit', "}\n\n.XOwIb_ddES:after{bottom:0;content:\"\";height:0.25rem;inset-inline-start:0;opacity:0;position:absolute;transform:translateZ(0) scaleX(0.01);width:100%}\n\n[dir=ltr] .XOwIb_ddES:after{left:0}\n\n[dir=rtl] .XOwIb_ddES:after{right:0}\n\n.XOwIb_ddES[aria-disabled]{font-weight:400}\n\n.XOwIb_ddES:not([aria-selected]):not([aria-disabled]):hover{border-bottom-color:").concat(theme.defaultHoverBorderColor || 'inherit', "}\n\n.XOwIb_ddES[aria-selected]:after{animation-duration:0.2s;animation-fill-mode:forwards;animation-name:XOwIb_epeh;animation-timing-function:ease-out;background-color:").concat(theme.defaultSelectedBorderColor || 'inherit', "}");
  },
  'root': 'XOwIb_bGBk',
  'default': 'XOwIb_ddES',
  'secondary': 'XOwIb_eLeB',
  'selectedTab': 'XOwIb_epeh'
};
import theme from "./theme.js";
/**
---
parent: Tabs
id: Tabs.Tab
---
**/

var Tab = (_dec = deprecated('8.0.0', {
  selected: 'isSelected',
  disabled: 'isDisabled'
}), _dec2 = themeable(theme, styles), _dec(_class = _dec2(_class = (_temp = _class2 = /*#__PURE__*/function (_Component) {
  _inherits(Tab, _Component);

  var _super = _createSuper(Tab);

  function Tab() {
    var _this;

    _classCallCheck(this, Tab);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _this.handleClick = function (event) {
      var _this$props = _this.props,
          onClick = _this$props.onClick,
          index = _this$props.index,
          id = _this$props.id,
          disabled = _this$props.disabled,
          isDisabled = _this$props.isDisabled;

      if (disabled || isDisabled) {
        return;
      }

      onClick(event, {
        index: index,
        id: id
      });
    };

    _this.handleKeyDown = function (event) {
      var _this$props2 = _this.props,
          onKeyDown = _this$props2.onKeyDown,
          index = _this$props2.index,
          id = _this$props2.id,
          disabled = _this$props2.disabled,
          isDisabled = _this$props2.isDisabled;

      if (disabled || isDisabled) {
        return;
      }

      onKeyDown(event, {
        index: index,
        id: id
      });
    };

    return _this;
  }

  _createClass(Tab, [{
    key: "render",
    value: function render() {
      var _classnames;

      var _this$props3 = this.props,
          id = _this$props3.id,
          variant = _this$props3.variant,
          selected = _this$props3.selected,
          disabled = _this$props3.disabled,
          controls = _this$props3.controls,
          children = _this$props3.children,
          props = _objectWithoutProperties(_this$props3, ["id", "variant", "selected", "disabled", "controls", "children"]);

      var isSelected = selected || props.isSelected;
      var isDisabled = disabled || props.isDisabled;
      return /*#__PURE__*/React.createElement(View, Object.assign({}, passthroughProps(props), {
        as: "div",
        role: "tab",
        id: id,
        onClick: this.handleClick,
        onKeyDown: this.handleKeyDown,
        className: classnames((_classnames = {}, _defineProperty(_classnames, styles.root, true), _defineProperty(_classnames, styles[variant], true), _classnames)),
        "aria-selected": isSelected ? 'true' : null,
        "aria-disabled": isDisabled ? 'true' : null,
        "aria-controls": controls,
        tabIndex: isSelected && !isDisabled ? '0' : null,
        position: "relative",
        focusPosition: "inset"
      }), callRenderProp(children));
    }
  }]);

  Tab.displayName = "Tab";
  return Tab;
}(Component), _class2.propTypes = {
  variant: PropTypes.oneOf(['default', 'secondary']),
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  controls: PropTypes.string.isRequired,
  isDisabled: PropTypes.bool,
  isSelected: PropTypes.bool,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  /**
   * __Deprecated - use `isDisabled` instead__
   */
  disabled: PropTypes.bool,

  /**
   * __Deprecated - use `isSelected` instead__
   */
  selected: PropTypes.bool
}, _class2.defaultProps = {
  children: null,
  variant: 'default',
  isDisabled: false,
  disabled: void 0,
  isSelected: false,
  selected: void 0,
  onClick: function onClick(event, _ref) {
    var index = _ref.index,
        id = _ref.id;
  },
  onKeyDown: function onKeyDown(event, _ref2) {
    var index = _ref2.index,
        id = _ref2.id;
  }
}, _temp)) || _class) || _class);
export default Tab;
export { Tab };